import React, { useState, useRef } from 'react';
import axios from 'axios';
import './LoanAgreement.css';

const API_URL = `${process.env.REACT_APP_API_PATH}`;

function LoanAgreement() {
  const [loanAccountNo, setLoanAccountNo] = useState('');
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);

  const handleChange = (e) => {
    setLoanAccountNo(e.target.value);
  };

  const fetchAgreement = async () => {
    if (!loanAccountNo.trim()) {
      setError('Please enter a Loan Account Number');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}LoanAgreement/${loanAccountNo.trim()}`);
      if (res.data.success) {
        setAgreement(res.data.data);
      } else {
        setError(res.data.error || 'Failed to fetch agreement');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Loan agreement not found. Please check the account number.');
      } else {
        setError(err.response?.data?.error || 'Server error. Please try again.');
      }
      setAgreement(null);
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchAgreement();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="mfl-app">
      {/* Header */}
      <header className="mfl-header">
        <h1>🏦 Microfinance Loan Agreement</h1>
        <p>RBI Master Direction on Microfinance Loans (October 2024)</p>
      </header>

      <div className="mfl-container">
        {/* Form Section */}
        <div className="mfl-form-card">
          <div className="mfl-form-grid" style={{ gridTemplateColumns: '1fr auto' }}>
            <div className="mfl-form-group">
              <label>Loan Account Number</label>
              <input 
                type="text" 
                value={loanAccountNo} 
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter Loan Account Number (e.g., MFI-20260731-1234)"
                disabled={loading}
              />
            </div>
            <div className="mfl-form-group" style={{ justifyContent: 'flex-end' }}>
              <button 
                className="mfl-btn mfl-btn-primary" 
                onClick={fetchAgreement} 
                disabled={loading}
                style={{ height: '42px' }}
              >
                {loading ? '⏳ Loading...' : '🔍 Get Agreement'}
              </button>
            </div>
          </div>
          <div className="mfl-btn-group" style={{ marginTop: '8px' }}>
            {agreement && (
              <button className="mfl-btn mfl-btn-print" onClick={handlePrint}>
                🖨️ Print / PDF
              </button>
            )}
          </div>
          {error && <div className="mfl-error">{error}</div>}
        </div>

        {/* Agreement Report */}
        {agreement && !loading && (
          <div className="mfl-report" ref={reportRef} id="report">
            
            {/* ===== HEADER ===== */}
            <div className="mfl-report-header">
              <div>
                <h2>MICROFINANCE LOAN AGREEMENT</h2>
                <div className="mfl-sub">RBI Compliant • Master Direction on Microfinance Loans (Oct 2024)</div>
              </div>
              <div className="mfl-meta">
                <div><strong>Loan Agreement Number:</strong> {agreement.agreementNumber}</div>
                <div><strong>Date of Agreement:</strong> {agreement.agreementDate}</div>
                <div><strong>Place:</strong> {agreement.place}</div>
              </div>
            </div>

            {/* ===== LOAN SUMMARY ===== */}
            {agreement.groupId && (
              <div className="mfl-section">
                <h3>LOAN SUMMARY</h3>
                <div className="mfl-loan-summary">
                  <div><strong>Group ID:</strong> {agreement.groupId}</div>
                  <div><strong>Branch Code:</strong> {agreement.branchCode}</div>
                  <div><strong>Loan Type:</strong> {agreement.loanType || 'MICRO'}</div>
                  <div><strong>Member ID:</strong> {agreement.memberId}</div>
                  <div><strong>Contract Start:</strong> {agreement.dateContractStarts}</div>
                  <div><strong>Contract End:</strong> {agreement.dateContractEnds}</div>
                  <div><strong>Disbursed Amount:</strong> ₹{agreement.disbursedAmount?.toLocaleString()}</div>
                  <div><strong>Status:</strong> {agreement.isClosed ? '🔒 Closed' : '✅ Active'}</div>
                  <div><strong>Amortization Type:</strong> {agreement.amortType || 'Standard'}</div>
                </div>
              </div>
            )}

            {/* ===== PARTIES ===== */}
            <div className="mfl-section">
              <p className="mfl-intro-text">
                This Loan Agreement is entered into at <strong>{agreement.place}</strong> between:
              </p>

              <div className="mfl-party-section">
                <div className="mfl-party-block">
                  <div className="mfl-party-label">THE LENDER:</div>
                  <div className="mfl-party-content">
                    <strong>{agreement.lenderName}</strong><br />
                    A company/body corporate registered under <strong>{agreement.lenderRegistration}</strong>, 
                    having its registered office at <strong>{agreement.lenderAddress}</strong>.<br />
                    <em>(Hereinafter referred to as the "Lender," which expression shall, unless repugnant to the context, 
                    mean and include its successors and assigns.)</em>
                  </div>
                </div>

                <div className="mfl-party-block" style={{ marginTop: 16 }}>
                  <div className="mfl-party-label">AND</div>
                </div>

                <div className="mfl-party-block">
                  <div className="mfl-party-label">THE BORROWER(S):</div>
                  <div className="mfl-party-content">
                    <strong>{agreement.borrowerName}</strong><br />
                    {agreement.borrowerRelation} of <strong>{agreement.borrowerFatherSpouse}</strong>, 
                    by occupation <strong>{agreement.borrowerOccupation}</strong>, residing at <strong>{agreement.borrowerAddress}</strong>.<br /><br />
                    
                    {agreement.hasCoBorrower && (
                      <>
                        <strong>AND</strong><br />
                        <strong>{agreement.coBorrowerName}</strong><br />
                        {agreement.coBorrowerRelationship} to Primary Borrower, by occupation <strong>{agreement.coBorrowerOccupation}</strong>, 
                        residing at <strong>{agreement.coBorrowerAddress}</strong>.<br />
                      </>
                    )}
                    <em>(Hereinafter collectively referred to as the "Borrower," which expression shall, unless repugnant to the context, 
                    mean and include their legal heirs, successors, and assigns.)</em>
                  </div>
                </div>

                <div className="mfl-party-block" style={{ marginTop: 8 }}>
                  <div className="mfl-party-content" style={{ fontStyle: 'italic' }}>
                    (The Lender and the Borrower are hereinafter collectively referred to as the "Parties" and individually as a "Party.")
                  </div>
                </div>
              </div>
            </div>

            {/* ===== KEY FACT STATEMENT ===== */}
            <div className="mfl-section">
              <h3>KEY FACT STATEMENT (KFS)</h3>
              <p className="mfl-kfs-note">
                <em>This statement is an integral part of the Loan Agreement and must be provided to the Borrower before signing the Agreement.</em>
              </p>

              <div className="mfl-kfs-table">
                <div className="mfl-kfs-row mfl-header">
                  <div className="mfl-kfs-label">Loan Details</div>
                  <div className="mfl-kfs-value">Information</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">1. Loan Amount (Principal)</div>
                  <div className="mfl-kfs-value">₹ {agreement.formattedLoanAmount}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">2. Type of Interest</div>
                  <div className="mfl-kfs-value">{agreement.interestType}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">3. Annual Percentage Rate (APR)</div>
                  <div className="mfl-kfs-value">{agreement.apr}%</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">4. All-Inclusive Cost (Fees/Charges)</div>
                  <div className="mfl-kfs-value">₹ {agreement.allInclusiveCost.toLocaleString()}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">5. Total Interest Payable</div>
                  <div className="mfl-kfs-value">₹ {agreement.formattedTotalInterest}</div>
                </div>
                
                <div className="mfl-kfs-row mfl-highlight">
                  <div className="mfl-kfs-label">6. Total Repayment Amount</div>
                  <div className="mfl-kfs-value"><strong>₹ {agreement.formattedTotalRepayment}</strong></div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">7. Repayment Schedule</div>
                  <div className="mfl-kfs-value">{agreement.repaymentSchedule} (as per Annexure - A)</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">8. Number of Instalments</div>
                  <div className="mfl-kfs-value">{agreement.numberOfInstalments}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">9. Late Payment Penalty</div>
                  <div className="mfl-kfs-value">{agreement.latePaymentPenalty}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">10. Prepayment Charges</div>
                  <div className="mfl-kfs-value">{agreement.prepaymentCharges}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">11. Processing Fee</div>
                  <div className="mfl-kfs-value">₹ {agreement.processingFee.toLocaleString()}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">12. Insurance Premium</div>
                  <div className="mfl-kfs-value">₹ {agreement.insurancePremium.toLocaleString()}</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">13. Method of Disbursement</div>
                  <div className="mfl-kfs-value">Direct Credit to Borrower's Bank Account (Account No: {agreement.accountNumber})</div>
                </div>
                
                <div className="mfl-kfs-row">
                  <div className="mfl-kfs-label">14. Disbursement Date</div>
                  <div className="mfl-kfs-value">{agreement.disbursementDate}</div>
                </div>
              </div>
            </div>

            {/* ===== TERMS AND CONDITIONS ===== */}
            <div className="mfl-section">
              <h3>TERMS AND CONDITIONS</h3>

              <div className="mfl-term">
                <h4>1. PURPOSE OF THE LOAN</h4>
                <p>The Loan is granted for the purpose of <strong>{agreement.loanPurpose}</strong>. The Borrower shall utilize the loan amount solely for this stated purpose.</p>
              </div>

              <div className="mfl-term">
                <h4>2. DISBURSEMENT</h4>
                <p>The Lender agrees to disburse the loan amount as mentioned in the Key Fact Statement directly to the Borrower's bank account, subject to the completion of all formalities and submission of required documents.</p>
              </div>

              <div className="mfl-term">
                <h4>3. INTEREST AND FEES</h4>
                <p><strong>3.1. Interest Rate:</strong> The loan shall bear interest at the rate mentioned in the Key Fact Statement. The interest shall be calculated on a monthly reducing balance basis.</p>
                <p><strong>3.2. Fees and Charges:</strong> All applicable processing fees, insurance premiums, and other charges are detailed in the KFS. No other charges shall be levied unless expressly agreed to in writing by the Borrower.</p>
              </div>

              <div className="mfl-term">
                <h4>4. REPAYMENT TERMS</h4>
                <p><strong>4.1. Schedule:</strong> The Borrower shall repay the loan in <strong>{agreement.numberOfInstalments}</strong> instalments as per the repayment schedule attached hereto as Annexure - A.</p>
                <p><strong>4.2. Payment:</strong> All payments shall be made via the mandated collection mechanism (e.g., ECS, Auto-Debit, Cash Collection at the Branch/Center). The Borrower is responsible for ensuring sufficient funds are available in their account.</p>
                <p><strong>4.3. Grace Period:</strong> Recognizing the importance of flexibility, the Lender offers a Grace Period of <strong>{agreement.gracePeriod}</strong> for the first repayment, or a schedule that aligns with the Borrower's income cycles.</p>
              </div>

              <div className="mfl-term">
                <h4>5. LATE PAYMENT AND DEFAULT</h4>
                <p><strong>5.1. Late Payment Penalty:</strong> If the Borrower fails to pay an instalment on the due date, a late payment penalty of <strong>{agreement.latePaymentPenalty}</strong>.</p>
                <p><strong>5.2. Consequences of Default:</strong> If the Borrower defaults on the repayment terms, the Lender reserves the right to:</p>
                <ul>
                  <li>Report the default to Credit Information Companies (CICs).</li>
                  <li>Initiate recovery proceedings as per law, following fair and responsible recovery practices and providing prior notice.</li>
                </ul>
              </div>

              <div className="mfl-term">
                <h4>6. BORROWER REPRESENTATIONS AND UNDERTAKINGS</h4>
                <p><strong>6.1. Income:</strong> The Borrower confirms that the annual household income does not exceed ₹300,000, as per the definition under the RBI framework.</p>
                <p><strong>6.2. Existing Loans:</strong> The Borrower declares all existing loans (from MFIs, Banks, or Informal Sources) to the Lender.</p>
                <p><strong>6.3. Over-Indebtedness:</strong> The Borrower acknowledges the risk of over-indebtedness and undertakes to utilize the loan prudently.</p>
              </div>

              <div className="mfl-term">
                <h4>7. PREPAYMENT</h4>
                <p>The Borrower is permitted to prepay the loan in full or in part at any time without the imposition of any penalty or charges (<strong>{agreement.prepaymentCharges}</strong>).</p>
              </div>

              <div className="mfl-term">
                <h4>8. CHANGES IN TERMS</h4>
                <p>Any changes in interest rates or service charges shall be communicated to the Borrower by the Lender with a notice period of <strong>30 days</strong> and shall be applied only prospectively.</p>
              </div>

              <div className="mfl-term">
                <h4>9. RECOVERY AND GRIEVANCE REDRESSAL</h4>
                <p><strong>9.1. Recovery:</strong> The Lender shall follow a non-coercive and transparent recovery process. The name and contact details of the recovery agent (if any) shall be provided to the Borrower before the commencement of recovery proceedings.</p>
                <p><strong>9.2. Grievance Redressal:</strong> If the Borrower has any grievance regarding the loan or its recovery, they may contact the Grievance Redressal Officer of the Lender:</p>
                <div className="mfl-grievance-box">
                  <div><strong>Name:</strong> {agreement.grievanceOfficerName}</div>
                  <div><strong>Contact:</strong> {agreement.grievanceOfficerContact}</div>
                </div>
              </div>

              <div className="mfl-term">
                <h4>10. GOVERNING LAW AND JURISDICTION</h4>
                <p>This Agreement shall be governed by and construed in accordance with the laws of India. The Courts at <strong>{agreement.jurisdiction}</strong> shall have exclusive jurisdiction over any disputes arising out of or in connection with this Agreement.</p>
              </div>

              <div className="mfl-term">
                <h4>11. ENTIRE AGREEMENT</h4>
                <p>This Agreement, along with the Key Fact Statement and Annexure-A, constitutes the entire agreement between the Parties and supersedes all prior negotiations, representations, or agreements, whether oral or written.</p>
              </div>
            </div>

            {/* ===== REPAYMENT SCHEDULE ===== */}
            <div className="mfl-section">
              <h3>ANNEXURE - A: REPAYMENT SCHEDULE</h3>
              <div className="mfl-table-wrap">
                <table className="mfl-table">
                  <thead>
                    <tr>
                      <th>Instalment No.</th>
                      <th>Due Date</th>
                      <th>Instalment Amount</th>
                      <th>Principal Component</th>
                      <th>Interest Component</th>
                      <th>Balance Outstanding</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreement.repaymentScheduleItems.map((item, i) => (
                      <tr key={i} className={item.isOverdue ? 'mfl-overdue' : ''}>
                        <td>{item.instalmentNo}</td>
                        <td>{item.dueDate}</td>
                        <td>₹{item.instalmentAmount.toLocaleString()}</td>
                        <td>₹{item.principalComponent.toLocaleString()}</td>
                        <td>₹{item.interestComponent.toLocaleString()}</td>
                        <td>₹{item.balanceOutstanding.toLocaleString()}</td>
                        <td>
                          {item.paid === 'Y' ? '✅ Paid' : 
                           item.isOverdue ? '⚠️ Overdue' : '⏳ Pending'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="mfl-total">
                      <td colSpan="2"><strong>TOTALS</strong></td>
                      <td><strong>₹{agreement.formattedTotalRepayment}</strong></td>
                      <td><strong>₹{agreement.formattedLoanAmount}</strong></td>
                      <td><strong>₹{agreement.formattedTotalInterest}</strong></td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* ===== EXECUTION ===== */}
            <div className="mfl-section">
              <h3>EXECUTION OF AGREEMENT</h3>
              <p className="mfl-execution-text">
                <strong>IN WITNESS WHEREOF,</strong> the Parties hereto have executed this Loan Agreement on the day, month, and year first hereinabove written.
              </p>

              <div className="mfl-signatures">
                <div className="mfl-signature-box">
                  <div className="mfl-sig-label">SIGNED AND DELIVERED BY THE BORROWER(S):</div>
                  <div className="mfl-sig-line">________________________</div>
                  <div className="mfl-sig-details">
                    <strong>(Signature of Primary Borrower)</strong><br />
                    Name: {agreement.borrowerName}<br />
                    Date: {agreement.agreementDate}
                  </div>
                  {agreement.hasCoBorrower && (
                    <div style={{ marginTop: 20 }}>
                      <div className="mfl-sig-line">________________________</div>
                      <div className="mfl-sig-details">
                        <strong>(Signature of Co-Borrower)</strong><br />
                        Name: {agreement.coBorrowerName}<br />
                        Date: {agreement.agreementDate}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mfl-signature-box">
                  <div className="mfl-sig-label">SIGNED AND DELIVERED BY THE LENDER:</div>
                  <div className="mfl-sig-line">________________________</div>
                  <div className="mfl-sig-details">
                    <strong>(Authorised Signatory)</strong><br />
                    Name: [Authorized Officer]<br />
                    Designation: [Designation]<br />
                    Date: {agreement.agreementDate}
                  </div>
                </div>
              </div>

              <div className="mfl-witnesses">
                <div className="mfl-witness-box">
                  <div><strong>WITNESSES:</strong></div>
                  <div style={{ marginTop: 8 }}>
                    <div><strong>1. {agreement.witness1Name}</strong></div>
                    <div style={{ fontSize: 11, color: '#555' }}>{agreement.witness1Address}</div>
                    <div className="mfl-sig-line">Signature</div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div><strong>2. {agreement.witness2Name}</strong></div>
                    <div style={{ fontSize: 11, color: '#555' }}>{agreement.witness2Address}</div>
                    <div className="mfl-sig-line">Signature</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== DISCLAIMER ===== */}
            <div className="mfl-disclaimer">
              <strong>⚠️ Disclaimer:</strong> This document is a template generated for demonstration purposes only and does not constitute legal advice. 
              This agreement is compliant with RBI Master Direction on Microfinance Loans (October 2024). All parties should consult with a qualified 
              legal professional to ensure compliance with applicable laws and regulations in their specific jurisdiction.
            </div>

            {/* ===== FOOTER ===== */}
            <div className="mfl-footer">
              Generated on {new Date().toLocaleString()} &bull; Microfinance Loan Agreement &bull; {agreement.agreementNumber}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoanAgreement;